import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Calendar, Plus, Users, MapPin, Globe, Lock, Eye, Trash2, Edit,
  MoreVertical, CheckCircle, Clock, XCircle, Download, ChevronRight,
  Building2, LogOut, X, Menu, LayoutDashboard, Briefcase, ClipboardList,
  Trophy, Crown, UserPlus, Search, Star, BarChart3, CreditCard, Mail,
  Megaphone, ExternalLink, Monitor, Ticket, Image, TrendingUp, IndianRupee,
  Upload
} from 'lucide-react';
import axios from 'axios';
import { ExtraChargesFields, DEFAULT_CHARGES, chargesError } from '../components/events/ExtraChargesFields';
import { CompanyBrand } from '../components/company/CompanyBrand';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CompanyEventsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createMode, setCreateMode] = useState('individual');
  const [editEvent, setEditEvent] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showPromotion, setShowPromotion] = useState(null);
  const [promotionPricing, setPromotionPricing] = useState(null);
  const [salesInsights, setSalesInsights] = useState(null);
  const [showSalesFor, setShowSalesFor] = useState(null);
  const [showCoupons, setShowCoupons] = useState(null);

  const token = localStorage.getItem('company_token');
  const headers = { Authorization: `Bearer ${token}` };

  const navItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/company/dashboard' },
    { label: 'Job Postings', icon: Briefcase, path: '/company/jobs' },
    { label: 'Assessments', icon: ClipboardList, path: '/company/assessments' },
    { label: 'Hiring Challenges', icon: Trophy, path: '/company/hiring-challenges' },
    { label: 'Enterprise', icon: Crown, path: '/company/enterprise' },
    { label: 'Events', icon: Calendar, path: '/company/events', active: true },
    { label: 'Candidate Pool', icon: UserPlus, path: '/company/candidates' },
    { label: 'Email Center', icon: Mail, path: '/company/email-center' },
    { label: 'Talent Search', icon: Search, path: '/company/talent-search' },
    { label: 'Shortlisted', icon: Star, path: '/company/shortlisted' },
    { label: 'Analytics', icon: BarChart3, path: '/company/analytics' },
    { label: 'Subscription', icon: CreditCard, path: '/company/subscription' },
  ];

  useEffect(() => {
    if (!token) { navigate('/company/auth'); return; }
    const c = localStorage.getItem('company_data');
    if (c) setCompanyData(JSON.parse(c));
    fetchEvents();
    fetchPromotionPricing();
    // Handle promotion payment return
    const params = new URLSearchParams(window.location.search);
    const promoOrderId = params.get('promo_order_id');
    if (promoOrderId) {
      verifyPromotionPayment(promoOrderId);
    }
  }, []);

  const verifyPromotionPayment = async (orderId) => {
    try {
      // Find the event ID from the URL or just refresh events
      toast.success('Promotion payment received! Your event will be promoted shortly.');
      window.history.replaceState({}, '', '/company/events');
      fetchEvents();
    } catch { }
  };

  const fetchPromotionPricing = async () => {
    try {
      const res = await axios.get(`${API}/api/public/event-promotion-pricing`);
      setPromotionPricing(res.data);
    } catch {
      setPromotionPricing({ price: 49999, features: [
        'Social media post across EvalentX channels',
        'Email campaign to 50K+ EvalentX Arena users',
        'Indirect WhatsApp promotion',
        'Featured listing for 30 days'
      ]});
    }
  };

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/company/events`, { headers });
      setEvents(res.data.events || []);
    } catch (err) {
      if (err.response?.status === 401) navigate('/company/auth');
      else toast.error('Failed to load events');
    } finally { setLoading(false); }
  }, []);

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this event? All registrations will be removed.')) return;
    try {
      await axios.delete(`${API}/api/company/events/${eventId}`, { headers });
      toast.success('Event deleted');
      fetchEvents();
    } catch { toast.error('Failed to delete'); }
  };

  const fetchSalesInsights = async (eventId) => {
    if (showSalesFor === eventId) { setShowSalesFor(null); setSalesInsights(null); return; }
    try {
      const res = await axios.get(`${API}/api/company/events/${eventId}/sales-insights`, { headers });
      setSalesInsights(res.data);
      setShowSalesFor(eventId);
    } catch { toast.error('Failed to load insights'); }
  };

  const handleLogout = () => {
    localStorage.removeItem('company_token');
    localStorage.removeItem('company_data');
    navigate('/company/auth');
  };

  const filteredEvents = activeTab === 'all' ? events : events.filter(e => e.status === activeTab);

  const statusConfig = {
    draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600', icon: Edit },
    pending_approval: { label: 'Pending Approval', color: 'bg-amber-50 text-amber-700', icon: Clock },
    approved: { label: 'Approved', color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle },
    rejected: { label: 'Rejected', color: 'bg-red-50 text-red-700', icon: XCircle },
  };

  return (
    <div className="min-h-screen flex bg-[#f3f2ef]" data-testid="company-events-page">
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 w-72 lg:w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 lg:transform-none h-screen overflow-y-auto ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="h-16 px-5 flex items-center justify-between border-b border-gray-200 sticky top-0 bg-white z-10">
          <Link to="/company" className="flex items-center gap-3">
            <CompanyBrand />
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"><X size={20} className="text-[#666]" /></button>
        </div>
        <nav className="p-3">
          <p className="text-xs font-semibold text-[#666] uppercase tracking-wider px-3 mb-2">Navigation</p>
          <div className="space-y-0.5">
            {navItems.map(item => (
              <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${item.active ? 'bg-[#e8f4f8] text-[#0a66c2] font-medium' : 'text-[#666] hover:bg-gray-100 hover:text-[#191919]'}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                <item.icon size={18} strokeWidth={item.active ? 2.5 : 2} /><span>{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 bg-white">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-[#b24020] hover:bg-red-50 rounded-lg"><LogOut size={18} /><span>Sign Out</span></button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"><Menu size={22} className="text-[#666]" /></button>
            <h1 className="text-lg font-semibold text-[#191919]">Event Hosting</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setEditEvent(null); setCreateMode('team'); setShowCreate(true); }} className="flex items-center gap-2 bg-[#191919] hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="host-hackathon-btn">
              <Trophy size={16} /> Host a Hackathon
            </button>
            <button onClick={() => { setEditEvent(null); setCreateMode('individual'); setShowCreate(true); }} className="flex items-center gap-2 bg-[#0a66c2] hover:bg-[#004182] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="create-event-btn">
              <Plus size={16} /> Create Event
            </button>
          </div>
        </header>

        <div className="p-4 sm:p-6 max-w-6xl mx-auto">
          {/* Tab Filters */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
            {[
              { key: 'all', label: 'All Events' },
              { key: 'pending_approval', label: 'Pending' },
              { key: 'approved', label: 'Approved' },
              { key: 'rejected', label: 'Rejected' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.key ? 'bg-[#0a66c2] text-white' : 'bg-white text-[#666] hover:bg-gray-100 border border-gray-200'}`}
                data-testid={`tab-${tab.key}`}>
                {tab.label} {tab.key === 'all' ? `(${events.length})` : `(${events.filter(e => e.status === tab.key).length})`}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#0a66c2] border-t-transparent rounded-full animate-spin" /></div>
          ) : filteredEvents.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Calendar className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-[#191919] mb-2">No events yet</h3>
              <p className="text-sm text-[#666] mb-6">Create your first event to get started</p>
              <button onClick={() => setShowCreate(true)} className="bg-[#0a66c2] hover:bg-[#004182] text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors">Create Event</button>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredEvents.map(event => {
                const sc = statusConfig[event.status] || statusConfig.draft;
                const StatusIcon = sc.icon;
                return (
                  <div key={event.id} className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all p-5" data-testid={`event-card-${event.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-base font-semibold text-[#191919] truncate">{event.title}</h3>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sc.color}`}>
                            <StatusIcon size={12} />{sc.label}
                          </span>
                          {event.is_paid && <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">Paid</span>}
                          {event.visibility === 'private' && <Lock size={14} className="text-gray-400" />}
                        </div>
                        <div className="flex flex-wrap gap-4 text-xs text-[#666]">
                          <span className="flex items-center gap-1"><Calendar size={12} />{event.event_date || 'No date set'}</span>
                          <span className="flex items-center gap-1">{event.event_type === 'offline' ? <MapPin size={12} /> : <Globe size={12} />}{event.event_type === 'offline' ? (event.venue || 'Venue TBD') : 'Online'}</span>
                          <span className="flex items-center gap-1"><Users size={12} />{event.registration_count || 0} registered</span>
                          {event.is_paid && <span className="font-medium text-[#191919]">&#8377;{event.price}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {event.status === 'approved' && (
                          <>
                            <button onClick={() => window.open(`/events/${event.id}`, '_blank')} className="p-2 hover:bg-gray-100 rounded-lg text-[#666] hover:text-[#191919]" title="View Event Page"><ExternalLink size={16} /></button>
                            <button onClick={() => navigate(`/company/events/${event.id}/participants`)} className="p-2 hover:bg-gray-100 rounded-lg text-[#666] hover:text-[#191919]" title="Participants" data-testid={`view-participants-${event.id}`}><Users size={16} /></button>
                            {event.is_paid && (
                              <button onClick={() => setShowCoupons(event)} className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-[#0a66c2] rounded-lg text-xs font-medium transition-colors" title="Manage Coupons" data-testid={`coupons-event-${event.id}`}><Ticket size={14} /> Coupons</button>
                            )}
                            {event.promotion_status === 'none' && (
                              <button onClick={() => setShowPromotion(event)} className="flex items-center gap-1 px-2.5 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg text-xs font-medium transition-colors" title="Promote Event" data-testid={`promote-event-${event.id}`}><Megaphone size={14} /> Promote</button>
                            )}
                            {event.promotion_status === 'pending' && (
                              <span className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium"><Clock size={14} /> Promo Pending</span>
                            )}
                            {event.promotion_status === 'active' && (
                              <span className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium"><CheckCircle size={14} /> Promoted</span>
                            )}
                          </>
                        )}
                        <button onClick={() => { setEditEvent(event); setShowCreate(true); }} className="p-2 hover:bg-gray-100 rounded-lg text-[#666] hover:text-[#191919]" title="Edit" data-testid={`edit-event-${event.id}`}><Edit size={16} /></button>
                        <button onClick={() => handleDelete(event.id)} className="p-2 hover:bg-red-50 rounded-lg text-[#666] hover:text-red-600" title="Delete"><Trash2 size={16} /></button>
                      </div>
                    </div>
                    {/* Sales Insights Toggle for Paid Events */}
                    {event.is_paid && event.status === 'approved' && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <button onClick={() => fetchSalesInsights(event.id)}
                          className="text-xs font-medium text-[#0a66c2] hover:text-[#004182] flex items-center gap-1"
                          data-testid={`sales-insights-toggle-${event.id}`}>
                          <TrendingUp size={13} /> {showSalesFor === event.id ? 'Hide' : 'View'} Sales Insights
                        </button>
                        {showSalesFor === event.id && salesInsights && (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3" data-testid={`sales-insights-${event.id}`}>
                            <div className="bg-blue-50 rounded-lg p-3">
                              <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider">Total Registrations</p>
                              <p className="text-xl font-bold text-[#191919] mt-0.5">{salesInsights.total_registrations}</p>
                            </div>
                            <div className="bg-emerald-50 rounded-lg p-3">
                              <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">Paid</p>
                              <p className="text-xl font-bold text-[#191919] mt-0.5">{salesInsights.paid_registrations}</p>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-3">
                              <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Pending Payment</p>
                              <p className="text-xl font-bold text-[#191919] mt-0.5">{salesInsights.pending_payment}</p>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-3">
                              <p className="text-[10px] font-semibold text-purple-700 uppercase tracking-wider">Revenue</p>
                              <p className="text-xl font-bold text-[#191919] mt-0.5" data-testid="sales-revenue-total">&#8377;{(salesInsights.revenue_collected || 0).toLocaleString()}</p>
                              {(salesInsights.revenue_breakdown?.gateway_fee > 0 || salesInsights.revenue_breakdown?.gst > 0) && (
                                <p className="text-[10px] text-[#666] mt-1 leading-snug" data-testid="sales-revenue-breakdown">
                                  Fees &#8377;{salesInsights.revenue_breakdown.registration.toLocaleString()} · Gateway &#8377;{salesInsights.revenue_breakdown.gateway_fee.toLocaleString()} · GST &#8377;{salesInsights.revenue_breakdown.gst.toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Create/Edit Modal */}
      {showCreate && (
        <EventFormModal
          event={editEvent}
          defaultMode={createMode}
          onClose={() => { setShowCreate(false); setEditEvent(null); }}
          onSaved={() => { setShowCreate(false); setEditEvent(null); fetchEvents(); }}
          token={token}
        />
      )}

      {/* Promotion Package Modal */}
      {showPromotion && (
        <PromotionModal
          event={showPromotion}
          pricing={promotionPricing}
          token={token}
          onClose={() => setShowPromotion(null)}
          onPurchased={() => { setShowPromotion(null); fetchEvents(); }}
        />
      )}

      {/* Coupon Manager Modal */}
      {showCoupons && (
        <CouponManagerModal event={showCoupons} token={token} onClose={() => setShowCoupons(null)} />
      )}
    </div>
  );
};


// ─── Coupon Manager Modal ───
const emptyCoupon = {
  code: '', discount_type: 'percentage', discount_value: 0, max_discount: 0,
  usage_limit: 0, per_user_limit: 0, expiry_date: '', min_purchase: 0, is_active: true,
};

const CouponManagerModal = ({ event, token, onClose }) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, {} = new, {..} = edit
  const [form, setForm] = useState(emptyCoupon);
  const [saving, setSaving] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const base = `${API}/api/company/events/${event.id}/coupons`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(base, auth);
      setCoupons(res.data.coupons || []);
    } catch { toast.error('Failed to load coupons'); }
    finally { setLoading(false); }
  }, [event.id]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyCoupon); setEditing({}); };
  const openEdit = (c) => {
    setForm({ ...emptyCoupon, ...c, expiry_date: c.expiry_date ? c.expiry_date.slice(0, 10) : '' });
    setEditing(c);
  };

  const save = async () => {
    if (!form.code.trim()) { toast.error('Coupon code is required'); return; }
    if (Number(form.discount_value) <= 0) { toast.error('Discount value must be greater than 0'); return; }
    setSaving(true);
    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value) || 0,
      max_discount: Number(form.max_discount) || 0,
      usage_limit: Number(form.usage_limit) || 0,
      per_user_limit: Number(form.per_user_limit) || 0,
      expiry_date: form.expiry_date ? new Date(form.expiry_date + 'T23:59:59').toISOString() : '',
      min_purchase: Number(form.min_purchase) || 0,
      is_active: !!form.is_active,
    };
    try {
      if (editing?.id) await axios.put(`${base}/${editing.id}`, payload, auth);
      else await axios.post(base, payload, auth);
      toast.success('Coupon saved');
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save coupon'); }
    finally { setSaving(false); }
  };

  const toggle = async (c) => {
    try { await axios.put(`${base}/${c.id}`, { is_active: !c.is_active }, auth); load(); }
    catch { toast.error('Failed to update'); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try { await axios.delete(`${base}/${c.id}`, auth); toast.success('Coupon deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]';
  const labelCls = 'block text-xs font-semibold text-[#666] mb-1';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="coupon-manager-modal" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h3 className="text-base font-bold text-[#191919]">Coupon Management</h3>
            <p className="text-xs text-[#666]">{event.title} · &#8377;{event.price?.toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-[#666]"><X size={18} /></button>
        </div>

        <div className="p-5">
          {editing === null ? (
            <>
              <div className="flex justify-end mb-4">
                <button onClick={openNew} data-testid="add-coupon-btn" className="flex items-center gap-1.5 px-3 py-2 bg-[#0a66c2] hover:bg-[#004182] text-white rounded-lg text-sm font-medium"><Plus size={15} /> Add Coupon</button>
              </div>
              {loading ? (
                <p className="text-sm text-[#666] text-center py-8">Loading…</p>
              ) : coupons.length === 0 ? (
                <p className="text-sm text-[#999] text-center py-10">No coupons yet. Create one to offer discounts for this event.</p>
              ) : (
                <div className="space-y-2">
                  {coupons.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 border border-gray-200 rounded-lg p-3" data-testid={`coupon-row-${c.code}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-[#191919]">{c.code}</span>
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${c.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{c.is_active ? 'Active' : 'Inactive'}</span>
                        </div>
                        <p className="text-xs text-[#666] mt-0.5">
                          {c.discount_type === 'percentage' ? `${c.discount_value}% off` : `\u20B9${c.discount_value} off`}
                          {c.max_discount > 0 && c.discount_type === 'percentage' ? ` (max \u20B9${c.max_discount})` : ''}
                          {' · '}Used {c.used_count || 0}{c.usage_limit > 0 ? `/${c.usage_limit}` : ''}
                          {c.expiry_date ? ` · Expires ${new Date(c.expiry_date).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                      <button onClick={() => toggle(c)} title={c.is_active ? 'Deactivate' : 'Activate'} className={`p-1.5 rounded-lg hover:bg-gray-100 ${c.is_active ? 'text-emerald-600' : 'text-gray-400'}`} data-testid={`toggle-coupon-${c.code}`}>
                        {c.is_active ? <Eye size={15} /> : <XCircle size={15} />}
                      </button>
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-gray-100 text-[#666]" data-testid={`edit-coupon-${c.code}`}><Edit size={15} /></button>
                      <button onClick={() => del(c)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" data-testid={`delete-coupon-${c.code}`}><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Coupon Code *</label>
                  <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="EARLYBIRD" className={inputCls} data-testid="coupon-code-input" />
                </div>
                <div>
                  <label className={labelCls}>Discount Type *</label>
                  <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className={inputCls} data-testid="coupon-type-select">
                    <option value="percentage">Percentage (%)</option>
                    <option value="flat">Flat Amount (INR)</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Discount Value * {form.discount_type === 'percentage' ? '(%)' : '(\u20B9)'}</label>
                  <input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className={inputCls} data-testid="coupon-value-input" />
                </div>
                {form.discount_type === 'percentage' && (
                  <div>
                    <label className={labelCls}>Max Discount Limit (\u20B9, optional)</label>
                    <input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} placeholder="0 = no cap" className={inputCls} />
                  </div>
                )}
                <div>
                  <label className={labelCls}>Total Usage Limit</label>
                  <input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder="0 = unlimited" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Per-User Limit (optional)</label>
                  <input type="number" value={form.per_user_limit} onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })} placeholder="0 = unlimited" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Expiry Date (optional)</label>
                  <input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Min Purchase Value (\u20B9, optional)</label>
                  <input type="number" value={form.min_purchase} onChange={(e) => setForm({ ...form, min_purchase: e.target.value })} placeholder="0 = none" className={inputCls} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#191919]">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
              </label>
              <div className="flex gap-2 pt-2">
                <button onClick={save} disabled={saving} data-testid="save-coupon-btn" className="flex-1 py-2.5 bg-[#0a66c2] hover:bg-[#004182] text-white rounded-lg text-sm font-semibold disabled:opacity-50">{saving ? 'Saving…' : 'Save Coupon'}</button>
                <button onClick={() => setEditing(null)} className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-[#666] rounded-lg text-sm font-medium">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ─── Promotion Package Modal ───
const PromotionModal = ({ event, pricing, token, onClose, onPurchased }) => {
  const [purchasing, setPurchasing] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const features = pricing?.features || [
    'Social media post across EvalentX channels',
    'Email campaign to 50K+ EvalentX Arena users',
    'Indirect WhatsApp promotion',
    'Featured listing for 30 days'
  ];
  const price = pricing?.price || 49999;

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/api/company/events/${event.id}/create-promotion-payment`, {}, { headers });
      if (res.data.payment_session_id) {
        const { load } = await import('@cashfreepayments/cashfree-js');
        const cashfree = await load({ mode: 'production' });
        cashfree.checkout({
          paymentSessionId: res.data.payment_session_id,
          returnUrl: `${window.location.origin}/company/events?promo_order_id=${res.data.order_id}&status=success`
        });
      } else {
        // Mock/dev mode
        toast.success('Promotion activated!');
        onPurchased();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally { setPurchasing(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="promotion-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Megaphone size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#191919]">Promote Your Event</h3>
              <p className="text-xs text-[#666]">{event.title}</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-5 border border-purple-100 mb-5">
            <div className="flex items-baseline justify-between mb-4">
              <h4 className="font-bold text-[#191919]">Premium Promotion Package</h4>
              <div className="text-right">
                <span className="text-2xl font-extrabold text-purple-700">&#8377;{price.toLocaleString()}</span>
                <p className="text-[10px] text-[#666] uppercase">one-time</p>
              </div>
            </div>
            <ul className="space-y-2.5">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-[#666]">
                  <CheckCircle size={16} className="text-purple-500 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-[#666] mb-4">
            By purchasing, your event will be promoted to our network of 50K+ developers and featured on the platform.
          </p>

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-3 border border-gray-200 rounded-lg text-sm font-medium text-[#666] hover:bg-gray-50 transition-colors">
              Maybe Later
            </button>
            <button onClick={handlePurchase} disabled={purchasing}
              className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors flex items-center justify-center gap-2" data-testid="purchase-promotion-btn">
              {purchasing ? 'Processing...' : <>
                <CreditCard size={16} /> Pay &#8377;{price.toLocaleString()}
              </>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const HostingKindPicker = ({ value, onChange }) => (
  <div data-testid="hosting-kind-picker">
    <p className="block text-sm font-medium text-[#191919] mb-2">What are you hosting?</p>
    <div className="grid grid-cols-2 gap-3">
      {[
        { v: 'individual', icon: Calendar, title: 'Event', text: 'Workshops, meetups, webinars — people register individually' },
        { v: 'team', icon: Trophy, title: 'Hackathon', text: 'Teams with invites, team payments, tickets, winners showcase' },
      ].map(({ v, icon: Icon, title, text }) => (
        <button type="button" key={v} onClick={() => onChange(v)} data-testid={`hosting-kind-${v}`}
          className={`text-left p-4 rounded-lg border-2 transition-colors ${value === v ? 'border-[#0a66c2] bg-[#0a66c2]/5' : 'border-gray-200 hover:border-gray-300'}`}>
          <Icon size={20} className={value === v ? 'text-[#0a66c2]' : 'text-[#666]'} />
          <p className="font-semibold text-[#191919] mt-2 text-sm">{title}</p>
          <p className="text-xs text-[#666] mt-0.5">{text}</p>
        </button>
      ))}
    </div>
    {value === 'team' && <p className="text-xs text-[#0a66c2] mt-2">Team size, payment and invite settings are below, next to Paid Event.</p>}
  </div>
);

// ─── Team / Hackathon registration settings ───
const TeamSettingsFields = ({ form, setForm }) => {
  const isTeam = form.registration_mode === 'team';
  const inp = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]';
  const num = (k) => (e) => setForm({ ...form, [k]: parseInt(e.target.value) || 0 });
  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50/50" data-testid="team-settings-block">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#191919] mb-1">Registration type</label>
          <select value={form.registration_mode} onChange={e => setForm({ ...form, registration_mode: e.target.value })} className={inp} data-testid="registration-mode-select">
            <option value="individual">Individual</option>
            <option value="team">Team / Hackathon</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#191919] mb-1">Registration deadline (optional)</label>
          <input type="datetime-local" value={form.registration_deadline} onChange={e => setForm({ ...form, registration_deadline: e.target.value })} className={inp} data-testid="registration-deadline-input" />
        </div>
      </div>
      {isTeam && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-xs font-medium text-[#191919] mb-1">Min team size</label><input type="number" min={1} max={20} value={form.team_min_size} onChange={num('team_min_size')} className={inp} data-testid="team-min-size-input" /></div>
            <div><label className="block text-xs font-medium text-[#191919] mb-1">Max team size</label><input type="number" min={1} max={20} value={form.team_max_size} onChange={num('team_max_size')} className={inp} data-testid="team-max-size-input" /></div>
            <div><label className="block text-xs font-medium text-[#191919] mb-1">Max teams (0 = unlimited)</label><input type="number" min={0} value={form.max_teams} onChange={num('max_teams')} className={inp} data-testid="max-teams-input" /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#191919] mb-1">Who pays</label>
              <select value={form.team_payment_mode} onChange={e => setForm({ ...form, team_payment_mode: e.target.value })} className={inp} data-testid="team-payment-mode-select">
                <option value="leader">Leader pays for the team</option>
                <option value="per_member">Each member pays</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#191919] mb-1">Price applies</label>
              <select value={form.team_fee_type} disabled={form.team_payment_mode === 'per_member'} onChange={e => setForm({ ...form, team_fee_type: e.target.value })} className={inp} data-testid="team-fee-type-select">
                <option value="per_member">Per member</option>
                <option value="per_team">Flat per team</option>
                <option value="hybrid">Team base fee + per member</option>
              </select>
              {form.team_fee_type === 'hybrid' && form.team_payment_mode !== 'per_member' && (<>
                <label className="block text-xs font-medium text-[#191919] mt-2 mb-1">Team base fee (INR, charged once per team)</label>
                <input type="number" min={0} value={form.team_base_fee} onChange={e => setForm({ ...form, team_base_fee: parseFloat(e.target.value) || 0 })}
                  placeholder="Team base fee (INR)" className={inp} data-testid="team-base-fee-input" />
              </>)}
            </div>
            <div><label className="block text-xs font-medium text-[#191919] mb-1">Invite expiry (hours)</label><input type="number" min={1} max={720} value={form.invite_expiry_hours} onChange={num('invite_expiry_hours')} className={inp} data-testid="invite-expiry-input" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#191919]">
            <input type="checkbox" checked={form.allow_solo} onChange={e => setForm({ ...form, allow_solo: e.target.checked })} data-testid="allow-solo-checkbox" /> Allow solo participants (team of 1)
          </label>
        </>
      )}
    </div>
  );
};

// ─── Event Form Modal ───
const EventFormModal = ({ event, onClose, onSaved, token, defaultMode = 'individual' }) => {
  const isEdit = !!event;
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    event_type: event?.event_type || 'online',
    venue: event?.venue || '',
    venue_address: event?.venue_address || '',
    event_date: event?.event_date || '',
    event_end_date: event?.event_end_date || '',
    is_paid: event?.is_paid || false,
    price: event?.price || 0,
    visibility: event?.visibility || 'public',
    max_participants: event?.max_participants || 0,
    registration_mode: event?.registration_mode || defaultMode,
    team_min_size: event?.team_min_size || 2,
    team_max_size: event?.team_max_size || 4,
    allow_solo: event?.allow_solo || false,
    team_payment_mode: event?.team_payment_mode || 'leader',
    team_fee_type: event?.team_fee_type || 'per_member',
    team_base_fee: event?.team_base_fee || 0,
    invite_expiry_hours: event?.invite_expiry_hours || 72,
    max_teams: event?.max_teams || 0,
    registration_deadline: toLocalInput(event?.registration_deadline),
    extra_charges: { ...DEFAULT_CHARGES, ...(event?.extra_charges || {}) },
    categories: event?.categories || [],
    cover_image: event?.cover_image || '',
    page_sections: event?.page_sections || [
      { id: '1', type: 'hero', title: 'Welcome', content: '' },
      { id: '2', type: 'about', title: 'About This Event', content: '' },
      { id: '3', type: 'schedule', title: 'Schedule', content: '' },
    ],
  });
  const [saving, setSaving] = useState(false);
  const [catInput, setCatInput] = useState('');
  const [activeFormTab, setActiveFormTab] = useState('details');
  const [uploading, setUploading] = useState(false);
  const [galleryImages, setGalleryImages] = useState(event?.gallery_images || []);
  const [companyLogo, setCompanyLogo] = useState(event?.company_logo || '');
  const headers = { Authorization: `Bearer ${token}` };

  const handleImageUpload = async (file, imageType, eventId) => {
    if (!eventId) { toast.error('Save the event first, then upload images'); return null; }
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/api/company/events/${eventId}/upload-image?image_type=${imageType}`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${imageType} uploaded`);
      return res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      return null;
    } finally { setUploading(false); }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Event title is required'); return; }
    const chErr = form.is_paid ? chargesError(form.extra_charges) : '';
    if (chErr) { toast.error(chErr); return; }
    setSaving(true);
    try {
      const payload = { ...form, registration_deadline: form.registration_deadline ? new Date(form.registration_deadline).toISOString() : '' };
      if (isEdit) {
        await axios.put(`${API}/api/company/events/${event.id}`, payload, { headers });
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/api/company/events`, payload, { headers });
        toast.success('Event created & submitted for approval');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const addCategory = () => {
    if (catInput.trim() && !form.categories.includes(catInput.trim())) {
      setForm({ ...form, categories: [...form.categories, catInput.trim()] });
      setCatInput('');
    }
  };

  const addSection = () => {
    setForm({ ...form, page_sections: [...form.page_sections, { id: Date.now().toString(), type: 'custom', title: 'New Section', content: '' }] });
  };

  const updateSection = (idx, field, value) => {
    const updated = [...form.page_sections];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, page_sections: updated });
  };

  const removeSection = (idx) => {
    setForm({ ...form, page_sections: form.page_sections.filter((_, i) => i !== idx) });
  };

  const moveSection = (idx, dir) => {
    const arr = [...form.page_sections];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setForm({ ...form, page_sections: arr });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-8 pb-8 overflow-y-auto" data-testid="event-form-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#191919]">{isEdit ? 'Edit Event' : form.registration_mode === 'team' ? 'Host a Hackathon' : 'Create New Event'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>

        {/* Form Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {[
            { key: 'details', label: 'Details' },
            { key: 'media', label: 'Media' },
            { key: 'page', label: 'Page Builder' },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveFormTab(t.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeFormTab === t.key ? 'border-[#0a66c2] text-[#0a66c2]' : 'border-transparent text-[#666] hover:text-[#191919]'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {/* ─── Details Tab ─── */}
          {activeFormTab === 'details' && (
            <div className="space-y-5">
              <HostingKindPicker value={form.registration_mode} onChange={(v) => setForm({ ...form, registration_mode: v })} />
              <div>
                <label className="block text-sm font-medium text-[#191919] mb-1">Event Title *</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]" placeholder="e.g. Tech Innovators Summit 2026" data-testid="event-title-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#191919] mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2] h-48 resize-y font-mono leading-relaxed" placeholder={"Describe your event...\n\nFormatting tips:\n# Heading\n## Subheading\n- Bullet point\n**Bold text**\n\nBlank lines create paragraphs"} data-testid="event-desc-input" />
                <p className="text-xs text-gray-400 mt-1.5">Supports: <span className="font-medium text-gray-500"># Headings</span>, <span className="font-medium text-gray-500">- Bullets</span>, <span className="font-medium text-gray-500">**Bold**</span>, and blank lines for paragraphs.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#191919] mb-1">Event Type</label>
                  <select value={form.event_type} onChange={e => setForm({ ...form, event_type: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]" data-testid="event-type-select">
                    <option value="online">Online</option>
                    <option value="offline">Offline / Venue-Based</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#191919] mb-1">Visibility</label>
                  <select value={form.visibility} onChange={e => setForm({ ...form, visibility: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]" data-testid="event-visibility-select">
                    <option value="public">Public (visible on EvalentX Arena)</option>
                    <option value="private">Private (shared link only)</option>
                  </select>
                </div>
              </div>
              {form.event_type === 'offline' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#191919] mb-1">Venue Name</label>
                    <input value={form.venue} onChange={e => setForm({ ...form, venue: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]" placeholder="e.g. IIT Gandhinagar" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#191919] mb-1">Address</label>
                    <input value={form.venue_address} onChange={e => setForm({ ...form, venue_address: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]" placeholder="Full address" />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#191919] mb-1">Start Date & Time</label>
                  <input type="datetime-local" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]" data-testid="event-date-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#191919] mb-1">End Date & Time</label>
                  <input type="datetime-local" value={form.event_end_date} onChange={e => setForm({ ...form, event_end_date: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-[#191919] mb-2">
                    <input type="checkbox" checked={form.is_paid} onChange={e => setForm({ ...form, is_paid: e.target.checked })} className="rounded" data-testid="event-paid-checkbox" />
                    Paid Event
                  </label>
                  {form.is_paid && (
                    <input type="number" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]" placeholder="Price in INR" data-testid="event-price-input" />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#191919] mb-1">Max Participants (0 = unlimited)</label>
                  <input type="number" value={form.max_participants} onChange={e => setForm({ ...form, max_participants: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]" />
                </div>
              </div>
              {form.is_paid && <ExtraChargesFields value={form.extra_charges} price={form.price} onChange={(extra_charges) => setForm({ ...form, extra_charges })} />}
              <TeamSettingsFields form={form} setForm={setForm} />
              <div>
                <label className="block text-sm font-medium text-[#191919] mb-1">Categories</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {form.categories.map((c, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                      {c} <button onClick={() => setForm({ ...form, categories: form.categories.filter((_, j) => j !== i) })}><X size={12} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={catInput} onChange={e => setCatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]" placeholder="Add category (e.g. Tech, AI, Hackathon)" />
                  <button onClick={addCategory} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-[#666]">Add</button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#191919] mb-1">Cover Image URL</label>
                <input value={form.cover_image} onChange={e => setForm({ ...form, cover_image: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]" placeholder="https://..." />
              </div>
            </div>
          )}

          {/* ─── Media Tab ─── */}
          {activeFormTab === 'media' && (
            <div className="space-y-6" data-testid="media-tab-content">
              {!isEdit && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="media-save-first-notice">
                  <Clock size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Save your event first</p>
                    <p className="text-xs text-amber-600 mt-0.5">Image uploads require a saved event. Fill in the details tab and click "Create & Submit" first, then edit the event to upload images.</p>
                  </div>
                </div>
              )}

              {/* Event Banner */}
              <div>
                <label className="block text-sm font-medium text-[#191919] mb-1">Event Banner</label>
                <p className="text-xs text-[#666] mb-3">Main cover image shown on the event page and listings. Recommended: 1200x630px, max 5MB.</p>
                {form.cover_image && (
                  <div className="relative mb-3 rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                    <img src={form.cover_image} alt="Banner preview" className="w-full h-44 object-cover" onError={e => { e.target.style.display='none'; }} />
                    <button onClick={() => setForm({ ...form, cover_image: '' })}
                      className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-white rounded-lg shadow-sm" title="Remove banner">
                      <X size={14} className="text-red-500" />
                    </button>
                  </div>
                )}
                <label className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${isEdit ? 'border-gray-300 hover:border-[#0a66c2] hover:bg-blue-50/30' : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'}`}>
                  <Upload size={24} className="text-[#666] mb-2" />
                  <span className="text-sm font-medium text-[#666]">{uploading ? 'Uploading...' : 'Click to upload banner image'}</span>
                  <span className="text-xs text-gray-400 mt-1">JPEG, PNG, WebP, GIF</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" disabled={!isEdit || uploading}
                    data-testid="banner-upload-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const url = await handleImageUpload(file, 'banner', event?.id);
                      if (url) setForm(prev => ({ ...prev, cover_image: url }));
                      e.target.value = '';
                    }} />
                </label>
              </div>

              {/* Company Logo */}
              <div>
                <label className="block text-sm font-medium text-[#191919] mb-1">Company Logo</label>
                <p className="text-xs text-[#666] mb-3">Your company logo shown on the event page. Recommended: square, min 200x200px.</p>
                <div className="flex items-start gap-4">
                  {companyLogo && (
                    <div className="relative w-24 h-24 rounded-xl overflow-hidden border border-gray-200 bg-gray-50 flex-shrink-0">
                      <img src={companyLogo} alt="Logo preview" className="w-full h-full object-contain p-1" onError={e => { e.target.style.display='none'; }} />
                      <button onClick={() => setCompanyLogo('')}
                        className="absolute top-1 right-1 p-1 bg-white/90 hover:bg-white rounded-md shadow-sm" title="Remove logo">
                        <X size={12} className="text-red-500" />
                      </button>
                    </div>
                  )}
                  <label className={`flex-1 flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-5 cursor-pointer transition-colors ${isEdit ? 'border-gray-300 hover:border-[#0a66c2] hover:bg-blue-50/30' : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'}`}>
                    <Upload size={20} className="text-[#666] mb-1.5" />
                    <span className="text-sm font-medium text-[#666]">{uploading ? 'Uploading...' : 'Upload company logo'}</span>
                    <span className="text-xs text-gray-400 mt-0.5">JPEG, PNG, WebP, GIF</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" disabled={!isEdit || uploading}
                      data-testid="logo-upload-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const url = await handleImageUpload(file, 'logo', event?.id);
                        if (url) setCompanyLogo(url);
                        e.target.value = '';
                      }} />
                  </label>
                </div>
              </div>

              {/* Gallery Images */}
              <div>
                <label className="block text-sm font-medium text-[#191919] mb-1">Gallery Images</label>
                <p className="text-xs text-[#666] mb-3">Additional images (previous editions, venue photos, etc.). Max 5MB each.</p>
                {galleryImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {galleryImages.map((img, i) => (
                      <div key={i} className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-video">
                        <img src={img} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; }} />
                        <button onClick={() => setGalleryImages(prev => prev.filter((_, j) => j !== i))}
                          className="absolute top-1.5 right-1.5 p-1 bg-white/90 hover:bg-white rounded-md shadow-sm" title="Remove">
                          <X size={12} className="text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${isEdit ? 'border-gray-300 hover:border-[#0a66c2] hover:bg-blue-50/30' : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'}`}>
                  <Image size={24} className="text-[#666] mb-2" />
                  <span className="text-sm font-medium text-[#666]">{uploading ? 'Uploading...' : 'Add gallery image'}</span>
                  <span className="text-xs text-gray-400 mt-1">JPEG, PNG, WebP, GIF</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" disabled={!isEdit || uploading}
                    data-testid="gallery-upload-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const url = await handleImageUpload(file, 'gallery', event?.id);
                      if (url) setGalleryImages(prev => [...prev, url]);
                      e.target.value = '';
                    }} />
                </label>
              </div>
            </div>
          )}

          {/* ─── Page Builder Tab ─── */}
          {activeFormTab === 'page' && (
            <div className="space-y-4">
              <p className="text-sm text-[#666]">Build your event page by adding, editing, and reordering sections.</p>
              {form.page_sections.map((section, idx) => (
                <div key={section.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-400 w-5">{idx + 1}</span>
                      <select value={section.type} onChange={e => updateSection(idx, 'type', e.target.value)}
                        className="text-xs bg-white border border-gray-200 rounded px-2 py-1 text-[#666]">
                        <option value="hero">Hero</option>
                        <option value="about">About</option>
                        <option value="schedule">Schedule</option>
                        <option value="speakers">Speakers</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="p-1 hover:bg-white rounded text-[#666] disabled:opacity-30">&#9650;</button>
                      <button onClick={() => moveSection(idx, 1)} disabled={idx === form.page_sections.length - 1} className="p-1 hover:bg-white rounded text-[#666] disabled:opacity-30">&#9660;</button>
                      <button onClick={() => removeSection(idx)} className="p-1 hover:bg-red-50 rounded text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <input value={section.title} onChange={e => updateSection(idx, 'title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-2 focus:outline-none focus:border-[#0a66c2]" placeholder="Section title" />
                  <textarea value={section.content} onChange={e => updateSection(idx, 'content', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm h-20 resize-none focus:outline-none focus:border-[#0a66c2]" placeholder="Section content..." />
                </div>
              ))}
              <button onClick={addSection} className="w-full py-3 border-2 border-dashed border-gray-300 hover:border-[#0a66c2] rounded-xl text-sm font-medium text-[#666] hover:text-[#0a66c2] transition-colors flex items-center justify-center gap-2">
                <Plus size={16} /> Add Section
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-[#666] hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-6 py-2.5 bg-[#0a66c2] hover:bg-[#004182] text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors" data-testid="save-event-btn">
            {saving ? 'Saving...' : isEdit ? 'Update Event' : 'Create & Submit for Approval'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyEventsPage;
